import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  Package,
  Receipt,
  Search,
  ShoppingCart,
  UserPlus,
  UserRound,
  Users,
} from "lucide-react";
import { Badge, ListPage } from "@redipos/ui";
import type { ConsoleTenantListItem } from "@redipos/contracts";
import { operacionesApi } from "../api.js";

type SortKey = "createdAt" | "name" | "productos" | "comprobantes" | "clientes" | "usuarios";
type SortDir = "asc" | "desc";

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("es-PY", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatRelativeDate(value: string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  const diffDays = Math.round((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (Math.abs(diffDays) === 0) return "Hoy";
  const rtf = new Intl.RelativeTimeFormat("es", { numeric: "auto" });
  if (Math.abs(diffDays) < 30) return rtf.format(diffDays, "day");
  const diffMonths = Math.round(diffDays / 30);
  if (Math.abs(diffMonths) < 12) return rtf.format(diffMonths, "month");
  return rtf.format(Math.round(diffDays / 365), "year");
}

function formatCount(value: number) {
  return value.toLocaleString("es-PY");
}

function SortButton({
  label,
  active,
  dir,
  onClick,
  align = "left",
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right" | "center";
}) {
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  const alignClass =
    align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 font-medium uppercase tracking-wide text-xs text-fg-subtle hover:text-fg transition-colors ${alignClass} w-full`}
    >
      {label}
      <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? "text-brand-500" : "opacity-50"}`} />
    </button>
  );
}

function UsageStat({
  icon: Icon,
  label,
  value,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  title?: string;
}) {
  return (
    <div className="flex items-center gap-1.5 min-w-0" title={title ?? `${label}: ${formatCount(value)}`}>
      <Icon className="w-3.5 h-3.5 text-fg-subtle shrink-0" />
      <span className="text-fg-muted tabular-nums">{formatCount(value)}</span>
      <span className="text-[11px] text-fg-subtle truncate hidden xl:inline">{label}</span>
    </div>
  );
}

export default function TenantsPage() {
  const [rows, setRows] = useState<ConsoleTenantListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  useEffect(() => {
    operacionesApi
      .tenants()
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(s) ||
        (t.slug || "").toLowerCase().includes(s) ||
        (t.owner?.email || "").toLowerCase().includes(s) ||
        (t.ruc || "").toLowerCase().includes(s) ||
        (t.ciudad || "").toLowerCase().includes(s),
    );
  }, [rows, q]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case "name":
          return dir * (a.name || "").localeCompare(b.name || "", "es");
        case "productos":
          return dir * (a.stats.productos - b.stats.productos);
        case "comprobantes":
          return dir * (a.stats.comprobantes - b.stats.comprobantes);
        case "clientes":
          return dir * (a.stats.clientes - b.stats.clientes);
        case "usuarios":
          return dir * (a.stats.usuarios - b.stats.usuarios);
        case "createdAt":
        default:
          return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      }
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "name" ? "asc" : "desc");
  };

  const activeCount = useMemo(() => filtered.filter((t) => t.isActive !== false).length, [filtered]);
  const suspendedCount = useMemo(() => filtered.filter((t) => t.isActive === false).length, [filtered]);
  const usageTotals = useMemo(
    () =>
      filtered.reduce(
        (acc, t) => ({
          productos: acc.productos + t.stats.productos,
          comprobantes: acc.comprobantes + t.stats.comprobantes,
          clientes: acc.clientes + t.stats.clientes,
        }),
        { productos: 0, comprobantes: 0, clientes: 0 },
      ),
    [filtered],
  );

  const metrics = [
    { key: "total", label: "Organizaciones", value: loading ? "—" : filtered.length },
    { key: "active", label: "Activas", value: loading ? "—" : activeCount },
    { key: "suspended", label: "Suspendidas", value: loading ? "—" : suspendedCount },
    {
      key: "productos",
      label: "Productos (total)",
      value: loading ? "—" : formatCount(usageTotals.productos),
    },
    {
      key: "ventas",
      label: "Ventas (total)",
      value: loading ? "—" : formatCount(usageTotals.comprobantes),
    },
  ];

  return (
    <ListPage
      title="Clientes"
      subtitle="Vista consolidada de cada organización: registro, plan y uso de la plataforma"
      metrics={metrics}
      sectionTitle="Listado de organizaciones"
      sectionSubtitle="Métricas de uso sin abrir el panel del cliente"
      actions={
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
          <div className="relative max-w-md w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-subtle" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar nombre, slug, email, RUC o ciudad…"
              className="w-full pl-10 pr-3 py-2 rounded-input bg-surface border border-border text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
            />
          </div>
          <Link
            to="/clientes/nuevo"
            className="inline-flex items-center justify-center gap-1.5 rounded-input bg-brand-700 px-3 py-2 text-sm font-medium text-fg-on-brand hover:bg-brand-600 whitespace-nowrap"
          >
            <UserPlus className="w-4 h-4" />
            Nuevo cliente
          </Link>
        </div>
      }
    >
      <div className="table-x-scroll">
        <table className="w-full text-sm min-w-[960px]">
          <thead>
            <tr className="bg-surface-elevated text-left border-b border-border">
              <th className="px-4 py-3">
                <SortButton
                  label="Organización"
                  active={sortKey === "name"}
                  dir={sortDir}
                  onClick={() => toggleSort("name")}
                />
              </th>
              <th className="px-4 py-3 w-36">
                <SortButton
                  label="Registro"
                  active={sortKey === "createdAt"}
                  dir={sortDir}
                  onClick={() => toggleSort("createdAt")}
                />
              </th>
              <th className="px-4 py-3">
                <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">
                  Uso en plataforma
                </span>
              </th>
              <th className="px-4 py-3 w-28">
                <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Plan</span>
              </th>
              <th className="px-4 py-3 w-28">
                <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Estado</span>
              </th>
              <th className="px-4 py-3 w-28 text-right">
                <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle"> </span>
              </th>
            </tr>
            <tr className="bg-surface-muted/40 text-left border-b border-border">
              <th colSpan={2} className="px-4 py-1.5" />
              <th className="px-4 py-1.5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(
                    [
                      ["productos", "Productos", Package],
                      ["comprobantes", "Ventas", ShoppingCart],
                      ["clientes", "Clientes", UserRound],
                      ["usuarios", "Usuarios", Users],
                    ] as const
                  ).map(([key, label, Icon]) => (
                    <SortButton
                      key={key}
                      label={label}
                      active={sortKey === key}
                      dir={sortDir}
                      onClick={() => toggleSort(key)}
                      align="left"
                    />
                  ))}
                </div>
              </th>
              <th colSpan={3} className="px-4 py-1.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-fg-subtle">
                  Cargando…
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-fg-subtle">
                  Sin resultados
                </td>
              </tr>
            ) : (
              sorted.map((t) => (
                <tr key={t.id} className="hover:bg-surface-elevated/60 transition-colors align-top">
                  <td className="px-4 py-3">
                    <p className="font-medium text-fg">{t.name}</p>
                    <p className="text-xs text-fg-subtle mt-0.5">{t.owner?.email || "Sin contacto"}</p>
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[11px] text-fg-subtle">
                      {t.slug ? <span>{t.slug}</span> : null}
                      {t.ruc ? <span>RUC {t.ruc}</span> : null}
                      {t.ciudad ? <span>{t.ciudad}</span> : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-fg-muted whitespace-nowrap">
                    <p>{formatDate(t.createdAt)}</p>
                    <p className="text-[11px] text-fg-subtle mt-0.5">{formatRelativeDate(t.createdAt)}</p>
                    {t.onboardingCompletedAt ? (
                      <p className="text-[11px] text-success mt-1" title="Onboarding completado">
                        Onboarded {formatDate(t.onboardingCompletedAt)}
                      </p>
                    ) : (
                      <p className="text-[11px] text-warning mt-1">Onboarding pendiente</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2">
                      <UsageStat icon={Package} label="Prod." value={t.stats.productos} />
                      <UsageStat
                        icon={ShoppingCart}
                        label="Ventas"
                        value={t.stats.comprobantes}
                        title={`${formatCount(t.stats.comprobantes)} comprobantes · ${formatCount(t.stats.ventas)} líneas`}
                      />
                      <UsageStat icon={UserRound} label="Cli." value={t.stats.clientes} />
                      <UsageStat icon={Users} label="Usu." value={t.stats.usuarios} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-fg-muted">
                    {t.subscription?.plan?.name || <span className="text-fg-subtle">Sin plan</span>}
                    {t.subscription?.status ? (
                      <p className="text-[11px] text-fg-subtle mt-0.5 capitalize">
                        {t.subscription.status.replace("_", " ")}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    {t.isActive === false ? (
                      <Badge variant="danger">Suspendida</Badge>
                    ) : (
                      <Badge variant="success">Activa</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link
                      to={`/clientes/${t.id}`}
                      className="inline-flex items-center gap-1 text-brand-500 hover:text-brand-400 text-sm font-medium"
                    >
                      Gestionar
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!loading && sorted.length > 0 ? (
        <p className="text-xs text-fg-subtle px-1 pt-3 flex items-start gap-1.5">
          <Receipt className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Ventas = comprobantes distintos. Pasa el cursor sobre el número para ver también las líneas vendidas.
        </p>
      ) : null}
    </ListPage>
  );
}
